import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function OrderSuccessPage() {
    const [params] = useSearchParams();
    const { clear } = useCart();
    const [state, setState] = useState({ status: "loading", order: null });

    useEffect(() => {
        const sessionId = params.get("session_id");
        const isPaypal = params.get("paypal");
        const paypalToken = params.get("token");

        async function resolve() {
            try {
                if (isPaypal && paypalToken) {
                    const r = await api.post(`/checkout/paypal/capture?paypal_order_id=${paypalToken}`);
                    setState({ status: r.data.status === "COMPLETED" ? "paid" : "pending", order: r.data });
                    clear();
                    return;
                }
                if (sessionId) {
                    // poll stripe status
                    for (let i = 0; i < 8; i++) {
                        const r = await api.get(`/checkout/status/${sessionId}`);
                        if (r.data.payment_status === "paid") {
                            setState({ status: "paid", order: r.data });
                            clear();
                            return;
                        }
                        await new Promise((res) => setTimeout(res, 2000));
                    }
                    setState({ status: "pending", order: null });
                    return;
                }
                setState({ status: "error", order: null });
            } catch {
                setState({ status: "error", order: null });
            }
        }
        resolve();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-24 text-center" data-testid="order-success-page">
            {state.status === "loading" && (
                <div data-testid="order-success-loading" className="flex flex-col items-center gap-4 text-slate-400">
                    <Loader2 className="w-10 h-10 animate-spin" />
                    Confirmation du paiement…
                </div>
            )}
            {state.status === "paid" && (
                <div className="fade-in-up">
                    <CheckCircle2 className="w-16 h-16 mx-auto text-slate-200 mb-6" />
                    <h1 className="font-serif-luxe text-4xl chrome-text font-semibold">Merci.</h1>
                    <p className="mt-4 text-slate-400">
                        Votre commande <span data-testid="order-success-number" className="text-slate-100 font-semibold">{state.order?.order_number}</span> est confirmée.
                    </p>
                    <p className="mt-2 text-sm text-slate-500">
                        Un courriel de confirmation vient de vous être envoyé. Vous recevrez le numéro de suivi Postes Canada dès l'expédition.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link to="/suivi" data-testid="order-success-track-link" className="btn-primary">Suivre ma commande</Link>
                        <Link to="/boutique" data-testid="order-success-continue-link" className="btn-ghost">Continuer mes achats</Link>
                    </div>
                </div>
            )}
            {state.status === "pending" && (
                <div data-testid="order-success-pending" className="text-slate-400">
                    Paiement en cours de confirmation. Vous recevrez un courriel dès validation.
                </div>
            )}
            {state.status === "error" && (
                <div data-testid="order-success-error" className="text-slate-400">
                    Impossible de confirmer le paiement. Contactez-nous via la page Contact.
                </div>
            )}
        </div>
    );
}
